import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ApiService } from "../../api/api-service";
import { Student } from "../types/type.student";
import { showNotification } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";

// Prefer the server's message (e.g. "Email already exists") over a generic one
const getErrorMessage = (err: unknown, fallback: string): string => {
    if (axios.isAxiosError(err)) {
        return err.response?.data?.error || fallback;
    }
    return fallback;
};

export const useStudentData = () => {
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchStudents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ApiService.getInstance().get("/students");
            const sortedStudents = response.data.data.sort(
                (a: Student, b: Student) => Number(b.student_id) - Number(a.student_id)
            );
            setStudents(sortedStudents);
        } catch (err) {
            setError("Failed to fetch students data");
            showNotification({
                title: "Error",
                message: "Failed to load students data",
                color: "red",
                icon: <IconX />,
            });
        } finally {
            setLoading(false);
        }
    }, []);

    // student_id is generated by the server from the TC_Counters "stud_id" record
    const createStudent = useCallback(async (studentData: Student) => {
        setLoading(true);
        try {
            const { student_id, ...payload } = studentData; // never send an ID on create
            const response = await ApiService.getInstance().post("/students", payload);
            showNotification({
                title: "Success",
                message: `Student created with ID ${response.data.student_id}`,
                color: "green",
                icon: <IconCheck />,
            });
            await fetchStudents();
            return true;
        } catch (err) {
            showNotification({
                title: "Error",
                message: getErrorMessage(err, "Failed to create student"),
                color: "red",
                icon: <IconX />,
            });
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchStudents]);

    const updateStudent = useCallback(async (student: Student) => {
        setLoading(true);
        try {
            await ApiService.getInstance().put(`/students/${student.student_id}`, student);
            showNotification({
                title: "Success",
                message: "Student updated successfully",
                color: "green",
                icon: <IconCheck />,
            });
            await fetchStudents();
            return true;
        } catch (err) {
            showNotification({
                title: "Error",
                message: getErrorMessage(err, "Failed to update student"),
                color: "red",
                icon: <IconX />,
            });
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchStudents]);

    const deleteStudent = useCallback(async (studentId: string) => {
        setLoading(true);
        try {
            await ApiService.getInstance().delete(`/students/${studentId}`);
            showNotification({
                title: "Success",
                message: "Student deleted successfully",
                color: "green",
                icon: <IconCheck />,
            });
            await fetchStudents();
            return true;
        } catch (err) {
            showNotification({
                title: "Error",
                message: "Failed to delete student",
                color: "red",
                icon: <IconX />,
            });
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchStudents]);

    // excludeStudentId lets the edit form ignore the student's own email
    const checkEmailExists = useCallback((email: string, excludeStudentId?: string): boolean => {
        const normalized = email.trim().toLowerCase();
        return students.some(
            (student) =>
                student.email.toLowerCase() === normalized &&
                student.student_id !== excludeStudentId
        );
    }, [students]);

    useEffect(() => {
        fetchStudents();
    }, [fetchStudents]);

    return {
        students,
        loading,
        error,
        fetchStudents,
        createStudent,
        updateStudent,
        deleteStudent,
        checkEmailExists,
    };
};